import Router from 'next/router'

/** Query params stripped from return URLs and after magic-link sign-in. */
export const AUTH_STRIP_PARAMS = ['mlt', 'mlu', 's']

const STORAGE_KEY = 'winit:authRedirectState'

const PARKING_SEARCH_PATHS = ['/parking/submit', '/parking/results', '/parking/flow', '/parking/pay']

function isParkingPath(pathname) {
  return PARKING_SEARCH_PATHS.some((p) => pathname.includes(p))
}

function selectedTicketIds(rootStore) {
  const flowType = rootStore?.parkingFlow?.flow?.type
  const results = rootStore?.parkingFlow?.results
  if (!results) return []
  if (flowType === 'fight') {
    return results.fightSelected?.map((t) => t.objectId).filter(Boolean) ?? []
  }
  return results.paySelected?.map((t) => t.objectId).filter(Boolean) ?? []
}

/** Persist funnel context before auth so in-page social login can restore it. */
export function captureAuthPageState(rootStore) {
  if (typeof window === 'undefined' || !rootStore) return null

  const parking = rootStore.parkingFlow
  const sc = parking?.searchCriteria
  const flow = parking?.flow
  const results = parking?.results
  const pathname = window.location.pathname

  const state = {
    pathname,
    asPath: `${window.location.pathname}${window.location.search}${window.location.hash || ''}`,
    parking: sc
      ? {
          plateNumber: sc.plateNumber || '',
          plateState: sc.plateState || '',
          ticketId: sc.ticketId || '',
          type: sc.type,
        }
      : null,
    entryType: flow?.entryType ?? null,
    // setEntryType() / setType() wipe currentStepSlug — must re-apply after restore.
    flowType: flow?.type ?? null,
    currentStepSlug: flow?.currentStepSlug ?? null,
    paySelectedIds: results?.paySelected?.map((t) => t.objectId).filter(Boolean) ?? [],
    fightSelectedIds: results?.fightSelected?.map((t) => t.objectId).filter(Boolean) ?? [],
    refetchResults:
      pathname.includes('/parking/results') || pathname.includes('/parking/flow'),
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // sessionStorage unavailable — URL enrichment still helps magic links
  }

  return state
}

function mergeParkingParams(params, rootStore) {
  const sc = rootStore?.parkingFlow?.searchCriteria
  if (!sc) return params

  if (sc.plateNumber) params.set('plate', sc.plateNumber)
  if (sc.plateState) params.set('state', sc.plateState)
  if (sc.ticketId) params.set('ticketId', sc.ticketId)
  if (sc.type) params.set('searchType', sc.type)

  const flow = rootStore?.parkingFlow?.flow
  const ticketIds = selectedTicketIds(rootStore)
  if (ticketIds.length) {
    params.set('tickets', ticketIds.join(','))
  } else {
    params.delete('tickets')
  }

  if (flow?.type) {
    params.set('flowType', flow.type)
  } else {
    params.delete('flowType')
  }

  if (flow?.currentStepSlug) {
    params.set('step', flow.currentStepSlug)
  } else {
    params.delete('step')
  }

  return params
}

/**
 * Return URL for magic-link emails: current page + funnel query params, minus auth tokens.
 * Stay on /parking/flow when the user is mid-checkout so login does not dump them on results.
 */
export function buildEnrichedAuthReturnUrl(rootStore) {
  if (typeof window === 'undefined') return ''

  captureAuthPageState(rootStore)

  const ticketIds = selectedTicketIds(rootStore)
  // Mid-checkout must never email a /parking/results return URL.
  const pathname = ticketIds.length > 0 ? '/parking/flow' : window.location.pathname

  const params = new URLSearchParams(window.location.search)
  AUTH_STRIP_PARAMS.forEach((key) => params.delete(key))
  if (isParkingPath(pathname) || ticketIds.length > 0) {
    mergeParkingParams(params, rootStore)
  }

  const search = params.toString()
  const hash = window.location.hash || ''
  return `${window.location.origin}${pathname}${search ? `?${search}` : ''}${hash}`
}

export function stripMagicLinkParamsFromUrl(url) {
  if (!url) return url
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined)
    AUTH_STRIP_PARAMS.forEach((key) => parsed.searchParams.delete(key))
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url
  }
}

function applyParkingSearchFromQuery(rootStore, query) {
  const parking = rootStore?.parkingFlow
  const sc = parking?.searchCriteria
  if (!sc || !query) return false

  let applied = false
  if (query.plate) {
    sc.setPlateNumber(String(query.plate))
    applied = true
  }
  if (query.state) {
    sc.setPlateState(String(query.state))
    applied = true
  }
  if (query.ticketId) {
    sc.setTicketId(String(query.ticketId))
    applied = true
  }
  if (query.searchType) {
    sc.changeSearchType(String(query.searchType))
    applied = true
  }
  return applied
}

function checkoutStateFromQuery(query) {
  if (!query) return null

  const ticketIds = query.tickets
    ? String(query.tickets)
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : []

  const flowType = query.flowType ? String(query.flowType) : null
  const currentStepSlug = query.step ? String(query.step) : null

  if (!ticketIds.length && !flowType && !currentStepSlug) return null

  return {
    entryType: flowType === 'fight' ? 'fight' : flowType === 'pay' ? 'pay' : null,
    flowType,
    currentStepSlug,
    paySelectedIds: flowType === 'fight' ? [] : ticketIds,
    fightSelectedIds: flowType === 'fight' ? ticketIds : [],
  }
}

function loadSavedAuthPageState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    sessionStorage.removeItem(STORAGE_KEY)
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function restoreFlowStep(flow, saved) {
  if (!flow || !saved) return

  // setEntryType() clears stack + currentStepSlug. Only call when the value changes.
  if (saved.entryType && flow.setEntryType && flow.entryType !== saved.entryType) {
    flow.setEntryType(saved.entryType)
  }

  // setType rebuilds the step stack; pass the saved step so checkout is not reset to step 1.
  if (saved.flowType && flow.setType) {
    flow.setType(saved.flowType, saved.currentStepSlug || undefined)
  } else if (saved.currentStepSlug && flow.setStep) {
    flow.setStep(saved.currentStepSlug)
  }
}

function restoreTicketSelection(results, saved) {
  if (!results?.found?.length || !saved) return

  if (saved.paySelectedIds?.length && results.setPayableSelected) {
    const selected = results.found.filter((t) => saved.paySelectedIds.includes(t.objectId))
    if (selected.length) results.setPayableSelected(selected)
  }
  // fightSelected has no bulk setter — re-select via toggle helpers when present.
  if (saved.fightSelectedIds?.length && typeof results.toggleFightableTicket === 'function') {
    saved.fightSelectedIds.forEach((id) => {
      const ticket = results.found.find((t) => t.objectId === id)
      if (ticket && !results.fightSelected?.find((t) => t.objectId === id)) {
        results.toggleFightableTicket(ticket)
      }
    })
  }
}

/**
 * Restore parking search / results after auth (social login in-page or magic link return).
 */
export async function restoreAuthPageState(rootStore) {
  if (typeof window === 'undefined' || !rootStore) return

  const auth = rootStore.auth
  auth?.changeKey?.('isRestoringAuthPageState', true)

  try {
    const saved = loadSavedAuthPageState()
    const parking = rootStore.parkingFlow
    const sc = parking?.searchCriteria
    const query = Router.query || {}
    const fromQuery = checkoutStateFromQuery(query)
    // Prefer sessionStorage (same-tab); fall back to URL for magic-link new tabs.
    const checkout =
      saved?.flowType || saved?.currentStepSlug || saved?.paySelectedIds?.length
        ? saved
        : fromQuery

    if (saved?.parking && sc) {
      if (saved.parking.plateNumber) sc.setPlateNumber(saved.parking.plateNumber)
      if (saved.parking.plateState) sc.setPlateState(saved.parking.plateState)
      if (saved.parking.ticketId) sc.setTicketId(saved.parking.ticketId)
      if (saved.parking.type) sc.changeSearchType(saved.parking.type)
    }

    applyParkingSearchFromQuery(rootStore, query)
    restoreFlowStep(parking?.flow, checkout || {})

    const onFunnelPath =
      Router.pathname.includes('/parking/results') || Router.pathname.includes('/parking/flow')

    const shouldRefetch =
      (saved?.refetchResults || onFunnelPath || Boolean(fromQuery)) &&
      sc &&
      (sc.plateNumber || sc.ticketId) &&
      parking.results.found.length === 0

    if (shouldRefetch) {
      await sc.fetchTickets(Router.pathname, rootStore.ui.currentHost)
    }

    restoreTicketSelection(parking?.results, checkout || {})

    // Re-apply step after refetch / selection — setEntryType side effects are sticky.
    if (checkout?.currentStepSlug || checkout?.flowType) {
      restoreFlowStep(parking?.flow, checkout)
    }
  } finally {
    auth?.changeKey?.('isRestoringAuthPageState', false)
  }
}
